//@ts-strict-ignore
import { ResponsiveBar } from '@nivo/bar';
import React, { useMemo } from 'react';
import Chip from './Chip';
import { IAnalyticsColumn, IAnalyticsReportOption, IWrappedMeasure } from './SalesAnalyticsTypes';
import * as d3sc from 'd3-scale-chromatic';
import * as d3color from 'd3-color';
import numeral from 'numeral';
import { OrdinalColorScaleConfig } from '@nivo/colors';
import { BarLegendProps } from '@nivo/bar/dist/types/types';
import { AxisProps } from '@nivo/axes';
import { DashboardWidgetReportType } from '../../../graphql/schema/p4types';
import { cloneDeep } from '@apollo/client/utilities';

const AXIS_BOTTOM: AxisProps = {
  tickSize: 5,
  tickPadding: 5,
  tickRotation: 0,
  legend: 'Period',
  legendOffset: 36,
  legendPosition: 'middle',
};

type ISalesAnalyticsBarProps = {
  formState: {
    measure: IWrappedMeasure | undefined;
    columns: Array<IAnalyticsColumn>;
    previousColumns: Array<IAnalyticsColumn>;
    data: Array<any>; //TODO - Improve Type ({ description: string; difference: number; reference: string } not good enough).
    secondDataSet: Array<any>;
    reportType?: IAnalyticsReportOption;
    pivotRows?: Array<{ description: string }>;
  };
  height: number | `${string}px`;
  pivotRef: any;
  defaultLabel?: string;
  graphOnly: boolean;
  colorScheme?: OrdinalColorScaleConfig;
};

export const SalesAnalyticsBar = ({
  formState: {
    measure,
    columns,
    previousColumns,
    data: reportData,
    secondDataSet: previousData,
    reportType,
    pivotRows = [],
  },
  height,
  pivotRef,
  defaultLabel = 'No Description',
  graphOnly,
  colorScheme: defaultColors = { scheme: 'set3' },
}: ISalesAnalyticsBarProps) => {
  const { data, keys, indexBy, marginConfig, colors, axisBottomConfig, layout, extraProps } = useMemo(() => {
    let colorBy;
    let keys;
    let extraProps = {};
    let indexBy: ((node: any) => string) | string = (node) => node.reference || node.description; //Need to use a unique key if available...
    let layout: 'horizontal' | 'vertical' = 'horizontal';
    let maxReferenceLength = 10;
    let data = cloneDeep(reportData);
    let colors = defaultColors;
    const axisBottomConfig = { ...AXIS_BOTTOM };
    const marginConfig = { top: 10, right: 200, bottom: 50, left: 100 };
    if (graphOnly) {
      marginConfig.right = 20;
      marginConfig.bottom = 45;
      marginConfig.left = 50;
      axisBottomConfig.legend = null;
    }

    const measureFormat = measure?.value !== 'UNIT' ? '($0a)' : '(0a)';

    switch (reportType?.value) {
      case DashboardWidgetReportType.YearToDate:
      case DashboardWidgetReportType.ComparativeYearToDate:
        let minVariance = 0;
        let maxVariance = 0;
        data.forEach((entry) => {
          if (entry.description && entry.description.length > maxReferenceLength) {
            maxReferenceLength = entry.description.length;
          }
          if (entry.difference < minVariance) {
            minVariance = entry.difference;
          } else if (entry.difference > maxVariance) {
            maxVariance = entry.difference;
          }
          if (!entry.description) {
            entry.description = entry.reference || (data.length === 1 ? measure?.value : defaultLabel);
          }
        });
        const range = maxVariance - minVariance;
        marginConfig.left = maxReferenceLength * 4;

        keys = ['difference'];

        colorBy = ({ data }) => {
          const colorIndex = (data.difference - minVariance) / range;
          return d3sc.interpolateRdYlGn(data.difference < 0 ? colorIndex * 0.5 : 0.5 + colorIndex * 0.5);
        };
        axisBottomConfig.legend = 'Variance';

        data = data.reverse();

        colors = ({ data: node }) => {
          const colorIndex = (node.difference - minVariance) / range;
          return d3color
            .color(d3sc.interpolateRdYlGn(node.difference < 0 ? colorIndex * 0.5 : 0.5 + colorIndex * 0.5))
            .hex();
        };

        break;
      case DashboardWidgetReportType.Monthly:
        layout = 'vertical';
        indexBy = 'shortName';
        keys = data.map((entry) => entry.description || defaultLabel);
        colors = { scheme: 'set3' };
        colorBy = 'id';
        if (previousData) {
          previousData.forEach((entry) => keys.push(`Previous Year - ${entry.description || defaultLabel}`));
        }
        data = columns
          .filter((column) => column.key.includes('period_'))
          .map((column) => {
            data.forEach((entry) => {
              column[`${entry.description || defaultLabel}`] = parseFloat(entry[column.key] || 0);
            });
            if (previousData && previousColumns) {
              const previousColumn = previousColumns.find((prevCol) => prevCol.periodNumber === column.periodNumber);

              if (previousColumn) {
                previousData.forEach((entry) => {
                  column[`Previous Year - ${entry.description || defaultLabel}`] = parseFloat(
                    entry[previousColumn.key] || 0
                  );
                });
              }
            }

            return column;
          });

        extraProps = {
          label: (entry) => {
            return numeral(entry.value).format(measureFormat);
          },
          groupMode: 'grouped',
        };

        break;
      case DashboardWidgetReportType.Yearly:
        layout = 'vertical';
        indexBy = 'shortName';
        keys = data.map((entry) => entry.description || defaultLabel);
        colors = { scheme: 'set3' };
        colorBy = 'id';
        data = columns
          .filter((column) => column.key.includes('yearValueList'))
          .map((column) => {
            data.forEach((entry) => {
              if (Number.isInteger(column.index) && column.index >= 0) {
                column[entry.description || defaultLabel] = parseFloat(entry.yearValueList[column.index]?.value ?? 0);
              } else {
                console.warn('Column index has not been provided');
              }
            });

            return column;
          });

        extraProps = {
          label: (entry) => {
            return numeral(entry.value).format(measureFormat);
          },
          groupMode: 'grouped',
        };

        break;
      case DashboardWidgetReportType.Pivot:
        layout = 'vertical';
        indexBy = 'description';
        keys = ['sales', 'cost'];
        colorBy = 'id';
        data =
          pivotRows?.map((entry) => {
            return {
              ...entry,
              description: pivotRef.current?.state.dimensions
                .map((dimension) => {
                  return entry[dimension];
                })
                .filter((entry) => !!entry)
                .join(' - '),
            };
          }) || [];

        extraProps = {
          groupMode: 'grouped',
          label: (entry) => {
            return numeral(entry.value).format('($0a)');
          },
          tooltip: (entry) => {
            const value = numeral(entry.data[entry.id]).format('($0a)') || '0k';
            return (
              <div style={{ display: 'inline-block' }}>
                <Chip key={entry.id} color={entry.color} /> {`${entry.data.description} - ${value}`}
              </div>
            );
          },
        };
        axisBottomConfig.tickRotation = 30;
        axisBottomConfig.legendOffset = 50;
        marginConfig.bottom = 100;

        break;
      default:
        console.warn('Report Type not defined, nothing to render.');
        break;
    }

    return { data, keys, indexBy, marginConfig, colors, axisBottomConfig, layout, extraProps };
  }, [
    columns,
    defaultColors,
    defaultLabel,
    graphOnly,
    measure?.value,
    pivotRef,
    pivotRows,
    previousColumns,
    previousData,
    reportData,
    reportType?.value,
  ]);

  const legends: BarLegendProps[] = useMemo(() => {
    return graphOnly
      ? []
      : [
          {
            dataFrom: 'keys',
            anchor: 'bottom-right',
            direction: 'column',
            justify: false,
            translateX: 120,
            translateY: 0,
            itemsSpacing: 2,
            itemWidth: 100,
            itemHeight: 20,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolSize: 20,
            effects: [
              {
                on: 'hover',
                style: {
                  itemOpacity: 1,
                },
              },
            ],
          },
        ];
  }, [graphOnly]);

  const tooltipFunction = ({ data, id }) => {
    return (
      <div style={{ backgroundColor: 'white', padding: '6px', boxShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)' }}>
        {`${id}: `}
        <b>{data[id]}</b>
      </div>
    );
  };

  return (
    <div style={{ height: height, width: '100%' }}>
      <ResponsiveBar
        data={data}
        keys={keys}
        indexBy={indexBy}
        margin={marginConfig}
        padding={0.3}
        colors={colors}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        axisTop={null}
        axisRight={null}
        axisLeft={{
          tickValues: 3,
        }}
        axisBottom={axisBottomConfig}
        labelSkipWidth={50}
        labelSkipHeight={12}
        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        legends={legends}
        animate={true}
        motionConfig={{ damping: 15, friction: 90 }}
        layout={layout}
        tooltip={tooltipFunction}
        {...extraProps}
      />
    </div>
  );
};
